import React, { useState, useEffect } from 'react';
import { X, Key, Shield, Settings, Copy, CheckCircle } from 'lucide-react';
import { PlatformApiService } from '../services/platformApi';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateKey: (keyData: CreateKeyData) => Promise<{ key: string } | void>;
}

interface CreateKeyData {
  name: string;
  permissions: string[];
  usageLimit?: number;
  expiresIn?: number;
}

const permissions = PlatformApiService.getAvailablePermissions();

export const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  isOpen,
  onClose,
  onCreateKey
}) => {
  const [formData, setFormData] = useState<CreateKeyData>({
    name: '',
    permissions: ['video:create', 'video:join'],
    usageLimit: undefined,
    expiresIn: undefined
  });
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [generatedKey, setGeneratedKey] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Call the parent callback which will handle the API call
      const result = await onCreateKey(formData);
      
      // If the parent callback returns a key, use it
      if (result && result.key) {
        setGeneratedKey(result.key);
        setStep('success');
        
        // Start countdown for auto-close
        setCountdown(10);
        const countdownInterval = setInterval(() => {
          setCountdown((prev) => {
            if (prev === null || prev <= 1) {
              clearInterval(countdownInterval);
              handleClose();
              return null;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // Generate a placeholder if no key returned (shouldn't happen with real API)
        const newKey = `pk_live_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
        setGeneratedKey(newKey);
        setStep('success');
        
        // Start countdown for auto-close
        setCountdown(10);
        const countdownInterval = setInterval(() => {
          setCountdown((prev) => {
            if (prev === null || prev <= 1) {
              clearInterval(countdownInterval);
              handleClose();
              return null;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
      // Handle error - could show an error message in the modal
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleClose = () => {
    setStep('form');
    setFormData({
      name: '',
      permissions: ['video:create', 'video:join'],
      usageLimit: undefined,
      expiresIn: undefined
    });
    setGeneratedKey('');
    setCopied(false);
    setCountdown(null);
    onClose();
  };

  const togglePermission = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-light-bg-primary dark:bg-dark-bg-primary rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
                {step === 'form' ? 'Create API Key' : 'API Key Created'}
              </h2>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {step === 'form' 
                  ? 'Generate a new API key for your application'
                  : 'Save this key securely - you won\'t see it again'
                }
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {step === 'form' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Key Name */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Key Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Production Key, Development Key"
                  className="w-full px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-3">
                  Permissions
                </label>
                <div className="space-y-3">
                  {permissions.map((permission: { id: string; label: string; description: string }) => (
                    <div key={permission.id} className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id={permission.id}
                        checked={formData.permissions.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                        className="mt-1 h-4 w-4 text-primary-500 border-light-border dark:border-dark-border rounded focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={permission.id}
                          className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary cursor-pointer"
                        >
                          {permission.label}
                        </label>
                        <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                          {permission.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Usage Limit */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Usage Limit (Optional)
                </label>
                <input
                  type="number"
                  value={formData.usageLimit || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    usageLimit: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  placeholder="e.g., 10000"
                  className="w-full px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                  Maximum number of API calls per month. Leave empty for unlimited.
                </p>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Expires In (Optional)
                </label>
                <select
                  value={formData.expiresIn || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    expiresIn: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  className="w-full px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Never expires</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-light-border dark:border-dark-border">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!formData.name || formData.permissions.length === 0}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create API Key
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Success Message */}
              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-success" />
                </div>
                <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                  API Key Created Successfully!
                </h3>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">
                  Your new API key has been generated. Make sure to copy it now - you won't be able to see it again.
                </p>
              </div>

              {/* Generated Key */}
              <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4 border border-light-border dark:border-dark-border">
                <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                  Your API Key
                </label>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-3 py-2 rounded font-mono text-sm text-light-text-primary dark:text-dark-text-primary border border-light-border dark:border-dark-border">
                    {generatedKey}
                  </code>
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 flex items-center space-x-1"
                  >
                    {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              {/* Security Warning */}
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-warning mt-0.5" />
                  <div>
                    <h4 className="font-medium text-warning mb-1">Important Security Notice</h4>
                    <ul className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1">
                      <li>• Store this key securely in your environment variables</li>
                      <li>• Never expose it in client-side code or public repositories</li>
                      <li>• You can regenerate or delete this key at any time</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end">
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  {countdown ? `Done (auto-close in ${countdown}s)` : 'Done'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};